import { BaseTheme, styled } from 'opensolar-ui'
import WarehouseLocatorModal from 'pages/ordering/stockAvailability/WarehouseLocatorModal'
import { CSSProperties, forwardRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { ProjectType } from 'types/projects'
import { orgSelectors } from '../../../ducks/orgs'
import { useFeatureFlag } from '../../../util/featureFlags'
import { DistributorDataTypeV2 } from '../types'

const getProjectAddress = (): string | undefined => {
  try {
    const project = (window as any).projectForm?.getState()?.values as ProjectType | undefined
    if (project?.address) {
      return `${project.address}, ${project?.locality || ''} ${project?.state || ''} ${project?.zip || ''}`.trim()
    }
  } catch {}
  return undefined
}

const getOrgAddress = (
  org:
    | { address?: string | null; locality?: string | null; state?: string | null; zip?: string | null }
    | null
    | undefined
): string | undefined => {
  if (!org?.address) return undefined
  return `${org.address}, ${org?.locality || ''} ${org?.state || ''} ${org?.zip || ''}`.trim()
}

const locationCount = (data: DistributorDataTypeV2[]): number => data.flatMap((d) => d.stock_levels ?? []).length

const StyledLink = styled('button')(({ theme }) => ({
  ...BaseTheme.typography.body3,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: BaseTheme.palette.neutralGrey[1000],
  textDecoration: 'underline',
  fontFamily: 'inherit', // match parent; body3 has no fontFamily, buttons have UA font otherwise
}))

type AvailableStocksButtonProps = {
  style?: CSSProperties
  data: DistributorDataTypeV2[]
  selectedDistributor?: string
  /** When true, show "In stock elsewhere" (AC: out of stock in default location but in stock elsewhere) */
  inStockElsewhere?: boolean
  icon?: React.ReactNode
  /** Optional custom label to override the default label */
  label?: string
  productCode?: string
  productName?: string
  amplitudeEventType?: string
}

const AvailableStocksButton = forwardRef<HTMLDivElement, AvailableStocksButtonProps>(
  (
    { style, data, selectedDistributor, inStockElsewhere, icon, label, productCode, productName, amplitudeEventType },
    ref
  ) => {
    const translate = useTranslate()
    const [showDialog, setShowDialog] = useState(false)
    const showShopLocationModal = useFeatureFlag('shop_location_modal', 'on') ?? false
    const org = useSelector(orgSelectors.getOrg)
    const orgAddress = getOrgAddress(org)
    const orgCountry = org?.country?.name
    const projectAddress = getProjectAddress()

    const toggleDialog = (event?: React.MouseEvent) => {
      event?.stopPropagation()
      setShowDialog((prev) => !prev)
    }

    if (!showShopLocationModal) {
      return null
    }

    const count = locationCount(data)
    const defaultLabel = inStockElsewhere
      ? translate('In stock elsewhere')
      : count === 1
        ? translate('View location')
        : translate('View locations')
    const linkLabel = label ?? defaultLabel

    return (
      <>
        <StyledLink type="button" onClick={toggleDialog} style={style}>
          {icon ?? linkLabel}
        </StyledLink>
        <WarehouseLocatorModal
          ref={ref}
          data={data}
          open={showDialog}
          onClose={toggleDialog}
          selectedDistributor={selectedDistributor}
          projectAddress={projectAddress}
          orgAddress={orgAddress}
          orgCountry={orgCountry}
          productCode={productCode}
          productName={productName}
          amplitudeEventType={amplitudeEventType}
        />
      </>
    )
  }
)

export default AvailableStocksButton
